import React, { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { H1 } from '@fs/zion-ui'
import useLogPage from '../../hooks/useLogPage'
import { ErrorDetails } from '../BrightspotPreview'

import { useBrightspot } from '../BrightspotProvider'
import { PageSkeleton } from '../../components/_helper-components'
import BrightspotPageProvider, { useBrightspotPage } from '../../providers/BrightspotProviders/BrightspotPageProvider'
import useBrightspotQuery, { CONTENT_TYPE_QUERY_MAP } from './hooks/useBrightspotQuery'
import useSiteInfoPath, { getBrightspotPageUrl } from '../BrightspotSiteInfo'
import useUrlPreviewIdTypename from '../../hooks/useUrlPreviewIdTypename'

const Article = lazy(() => import('../../pages/ZionCMSHelpCenterArticle'))
const AuthorPage = lazy(() => import('../../pages/ZionCMSAuthorPage'))
const EventCalendarPage = lazy(() => import('../../pages/ZionCMSEventCalendarPage'))
const EventPage = lazy(() => import('../../pages/ZionCMSEventPage'))
const LessonPage = lazy(() => import('../../pages/ZionCMSLessonPage'))
const LocationPage = lazy(() => import('../../pages/ZionCMSLocationPage'))
const Page = lazy(() => import('../../pages/ZionCMSPage'))
const SearchPage = lazy(() => import('../../pages/ZionCMSSearchPage'))
const SOPPage = lazy(() => import('../../pages/ZionCMSSOPPage'))
const BlogPage = lazy(() => import('../../pages/ZionCMSBlogPage'))
const BlogPostPage = lazy(() => import('../../pages/ZionCMSBlogPostPage'))
const TagPage = lazy(() => import('../../pages/ZionCMSTagPage'))

type BrightspotPageProps = {
  /**
   * The URL assigned to the page on Brightspot. Should be fully qualified to match what is registered
   * with the site in Brightspot. For instance: https://beta.familysearch.org/zion-cms/demo-internal/page.
   * In this case the https://beta.familysearch.org/zion-cms matches the zion-cms-beta site and the
   * /demo-internal/page matches a page on that site.
   */
  url?: string
  /**
   * The preview ID for the page.
   */
  previewId?: string
  /** @deprecated - url or previewId is sufficient
   * The Brightspot site that BrightspotPage will use to fetch the page.
   */
  site?: string
  /** @deprecated - url or previewId is sufficient
   */
  trimUrl?: string
}

export default function BrightspotPage({ url, ...props }: BrightspotPageProps): JSX.Element {
  const { t } = useTranslation()
  const { previewId } = useUrlPreviewIdTypename()
  const {
    site: resolvedSite,
    relativeUrl,
    contentType,
    status,
  } = useSiteInfoPath({
    url: getBrightspotPageUrl(url),
    previewId,
  })

  // This effect is needed by react-router-hash-link to scroll to hash on a full page refresh
  useEffect(() => {
    const { hash } = window.location
    if (!hash) return

    const id = hash.replace('#', '')
    const scrollToElement = () => {
      const element = document.getElementById(id)
      if (element) {
        const yCoordinate = element.getBoundingClientRect().top + window.pageYOffset
        window.scrollTo({ top: yCoordinate, behavior: 'smooth' })
      }
    }

    scrollToElement()
    // Timeouts with extra scrolls help slower devices and are harmless to fast ones.
    ;[1000, 2000, 5000].forEach((delay) => setTimeout(scrollToElement, delay))
  }, [])

  switch (status) {
    case 'loading':
      return <PageSkeleton />
    case 'done':
      return (
        <BrightspotPageProvider
          resolvedSite={resolvedSite}
          relativeUrl={relativeUrl}
          contentType={contentType}
          status={status}
          previewId={previewId}
        >
          <Suspense fallback={<div>{t('common-ui:loading.ellipsis.label', 'Loading...')}</div>}>
            <InternalBrightspotPage />
          </Suspense>
        </BrightspotPageProvider>
      )
    case 'error':
    default:
      // eslint-disable-next-line no-case-declarations
      const pageError = new Error(
        `Error - No page content to render was found at: ${url}\n${JSON.stringify({ ...props })}`
      )
      return <ErrorDetails error={pageError} {...props} />
  }
}

function InternalBrightspotPage(): JSX.Element {
  const { resolvedSite, relativeUrl, contentType, previewId } = useBrightspotPage()
  const { customContentTypes } = useBrightspot()

  const typeMap = CONTENT_TYPE_QUERY_MAP.find((entry) => entry.contentType === contentType)
  if (!typeMap && customContentTypes) {
    // This might be a custom page type that we don't have a query for. Check to see if we have one registered.
    const customTypeMap = customContentTypes.find((entry) => entry.contentType === contentType)
    if (customTypeMap) {
      return customTypeMap.renderFunction({ resolvedSite, relativeUrl, contentType, previewId })
    }
    return <H1 size="H3">WARNING: Need a handler registered for contentType: {contentType}</H1>
  }

  return (
    <DefaultBrightspotPageHandler
      resolvedSite={resolvedSite}
      relativeUrl={relativeUrl}
      contentType={contentType}
      previewId={previewId}
    />
  )
}

function DefaultBrightspotPageHandler({ resolvedSite, relativeUrl, contentType, previewId }): JSX.Element {
  const [queryExecuted, setQueryExecuted] = useState<boolean>(false)
  const { executeQuery, loading, error, data } = useBrightspotQuery()

  useEffect(() => {
    if (!queryExecuted) {
      executeQuery()
      setQueryExecuted(true)
    }
  }, [queryExecuted, executeQuery])

  useLogPage({
    queryExecuted,
    previewId,
    resolvedSite: resolvedSite || undefined,
    relativeUrl: relativeUrl || undefined,
    contentType: contentType || undefined,
    loading,
    error,
    data,
  })

  let returnComponent: JSX.Element = <></>

  if (loading || !queryExecuted) return <PageSkeleton />

  if (error) {
    return <ErrorDetails error={error} previewId={previewId} />
  }

  if (data?.CustomPage?.page) {
    const customPage = data.CustomPage
    const {
      id,
      isDisableHeaderFooter,
      page,
      subNavigation,
      alertBannerMessage,
      alertBannerMessageRichText,
      alternateLocales,
      isAlertBannerActive,
      alertBannerStartDate,
      alertBannerEndDate,
      alertBannerTitle,
    } = customPage

    returnComponent = (
      <Page
        {...page}
        id={id}
        subNavigation={subNavigation}
        isDisableHeaderFooter={isDisableHeaderFooter}
        alertBannerMessage={alertBannerMessage}
        alertBannerMessageRichText={alertBannerMessageRichText}
        alternateLocales={alternateLocales}
        isAlertBannerActive={isAlertBannerActive}
        alertBannerStartDate={alertBannerStartDate}
        alertBannerEndDate={alertBannerEndDate}
        alertBannerTitle={alertBannerTitle}
      />
    )
  } else if (data?.EventPage) {
    const { id, ...other } = data.EventPage
    returnComponent = <EventPage id={id} {...other} />
  } else if (data?.EventCalendarPage) {
    returnComponent = <EventCalendarPage {...data?.EventCalendarPage} />
  } else if (data?.HelpCenterArticle) {
    const { id, ...other } = data.HelpCenterArticle
    returnComponent = <Article id={id} {...other} />
  } else if (data?.HelpCenterSearch) {
    returnComponent = <SearchPage {...data?.HelpCenterSearch} />
  } else if (data?.LessonDetail) {
    const { id, ...other } = data.LessonDetail
    returnComponent = <LessonPage id={id} {...other} />
  } else if (data?.LocationPage) {
    const { id, ...other } = data.LocationPage
    returnComponent = <LocationPage id={id} {...other} />
  } else if (data?.StandardOperatingProcedureDetail) {
    returnComponent = <SOPPage {...data?.StandardOperatingProcedureDetail} />
  } else if (data?.AuthorPage) {
    returnComponent = <AuthorPage {...data?.AuthorPage} />
  } else if (data?.BlogPostPage) {
    returnComponent = <BlogPostPage {...data?.BlogPostPage} />
  } else if (data?.BlogDetail) {
    returnComponent = <BlogPage {...data?.BlogDetail} />
  } else if (data?.TagPage) {
    returnComponent = <TagPage {...data?.TagPage} />
  } else {
    const pageError = new Error(
      `Error - No page content to render was found.\n\n${JSON.stringify({
        previewId,
        resolvedSite,
        relativeUrl,
        contentType,
      })}\n\n${JSON.stringify(data, null, 2)}`
    )

    return <ErrorDetails error={pageError} previewId={previewId} />
  }

  return returnComponent
}
